import {
  fromConsultationListDataList,
  fromDocumentListDataList,
  fromOutcomeTaskDataList,
  fromPatientCodesData,
  fromPatientRecordData,
  fromPatientReferralDataList,
} from '@medprompt/shared';

/**
 * Query string matching `PatientIdentifier.from(nhsId, patientId)` on the backend.
 */
export function patientIdentifierQuery(nhsId, patientId) {
  if (!nhsId || !patientId) {
    throw new Error('patientId cannot be null or blank');
  }
  return new URLSearchParams({
    nhs_id: nhsId,
    patient_id: patientId,
  }).toString();
}

export function patientViewPath(nhsId, patientId) {
  if (!nhsId || !patientId) {
    throw new Error('patientId cannot be null or blank');
  }
  return `/patient/${nhsId}?${new URLSearchParams({ patient_id: patientId }).toString()}`;
}

/**
 * PatientService - Handles patient search and patient-related data API operations
 */
export class PatientService {
  constructor(apiClient) {
    this.apiClient = apiClient;
    console.log('PatientService initialized');
  }

  /**
   * Search for patients by query string (name, NHS number, etc.)
   * @param {string} query - Search query
   * @returns {Promise<Array>} Array of Patient objects
   */
  async searchPatients(query) {
    try {
      const requestBody = { query: query };
      const data = await this.apiClient.post('/api/patients/search', requestBody);
      if (data === null || data === undefined) {
        console.error('searchPatients received null/undefined data');
        return [];
      }
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error searching patients:', error);
      throw error;
    }
  }

  /**
   * Fetch all documents for a patient by NHS ID
   * @param {string} nhsId - Patient NHS ID
   * @returns {Promise<Array>} Array of PatientDocumentData objects
   */
  async fetchPatientDocuments(nhsId) {
    try {
      const data = await this.apiClient.get(`/api/patient/${nhsId}/documents/fetch`);
      if (data === null || data === undefined) {
        console.error('fetchPatientDocuments received null/undefined data');
        return [];
      }
      return Array.isArray(data) ? fromDocumentListDataList(data) : [];
    } catch (error) {
      console.error('Error fetching patient documents:', error);
      throw error;
    }
  }

  /**
   * Fetch all outcome tasks for a patient by NHS ID
   * @param {string} nhsId - Patient NHS ID
   * @returns {Promise<Array>} Array of OutcomeTask objects
   */
  async fetchPatientTasks(nhsId) {
    try {
      const data = await this.apiClient.get(`/api/patient/${nhsId}/outcome_tasks/fetch`);
      if (data === null || data === undefined) {
        console.error('fetchPatientTasks received null/undefined data');
        return [];
      }
      return Array.isArray(data) ? fromOutcomeTaskDataList(data) : [];
    } catch (error) {
      console.error('Error fetching patient tasks:', error);
      throw error;
    }
  }

  /**
   * Fetch all referrals for a patient by NHS ID
   * @param {string} nhsId - Patient NHS ID
   * @returns {Promise<Array>} Array of PatientReferral objects
   */
  async fetchPatientReferrals(nhsId) {
    try {
      const data = await this.apiClient.get(`/api/patient/${nhsId}/referrals/fetch`);
      if (data === null || data === undefined) {
        console.error('fetchPatientReferrals received null/undefined data');
        return [];
      }
      return Array.isArray(data) ? fromPatientReferralDataList(data) : [];
    } catch (error) {
      console.error('Error fetching patient referrals:', error);
      throw error;
    }
  }

  /**
   * Fetch all consultations for a patient by NHS ID
   * @param {string} nhsId - Patient NHS ID
   * @returns {Promise<Array>} Array of PatientConsultationData objects
   */
  async fetchPatientConsultations(nhsId) {
    try {
      const data = await this.apiClient.get(`/api/patient/${nhsId}/consultations/fetch`);
      if (data === null || data === undefined) {
        console.error('fetchPatientConsultations received null/undefined data');
        return [];
      }
      return Array.isArray(data) ? fromConsultationListDataList(data) : [];
    } catch (error) {
      console.error('Error fetching patient consultations:', error);
      throw error;
    }
  }

  /**
   * Fetch patient record data (demographics, problems, document previews, pending referrals)
   * @param {string} nhsId - Patient NHS ID
   * @param {string} patientId - EMIS patient ID
   * @returns {Promise<Object>} PatientRecord object
   */
  async fetchPatientRecord(nhsId, patientId, options = {}) {
    try {
      const data = await this.apiClient.get(`/api/patient_record/data?${patientIdentifierQuery(nhsId, patientId)}`, options);
      if (data === null || data === undefined) {
        console.error('fetchPatientRecord received null/undefined data');
        return null;
      }
      return fromPatientRecordData(data);
    } catch (error) {
      console.error('Error fetching patient record:', error);
      throw error;
    }
  }

  /**
   * Fetch fresh patient problem codes from EMIS (1-hour cache).
   * @param {string} nhsId - Patient NHS ID
   * @param {string} patientId - EMIS patient ID
   * @returns {Promise<Object>} PatientCodesData
   */
  async fetchFreshPatientCodes(nhsId, patientId, options = {}) {
    try {
      const data = await this.apiClient.get(`/api/patient_record/patient_codes/fresh_data?${patientIdentifierQuery(nhsId, patientId)}`, options);
      if (data === null || data === undefined) {
        console.error('fetchFreshPatientCodes received null/undefined data');
        return null;
      }
      return fromPatientCodesData(data);
    } catch (error) {
      console.error('Error fetching fresh patient codes:', error);
      throw error;
    }
  }
}
